import React, { useState, useEffect } from 'react';
import { Settings, Plus, Sparkles, Layers, Package, FileCode, CheckCircle2, Grid } from 'lucide-react';
import { WorkType, Material, DynamicReportSchema } from '../../types';
import { ChessboardModule } from '../matrix/ChessboardModule';

export const AdminModule: React.FC = () => {
  const [activeAdminSubTab, setActiveAdminSubTab] = useState<'chessboard' | 'settings'>('chessboard');

  const [schemas, setSchemas] = useState<DynamicReportSchema[]>([]);
  const [workTypes, setWorkTypes] = useState<WorkType[]>([]);
  const [materials, setMaterials] = useState<Material[]>([]);

  // New schema draft
  const [newSchemaName, setNewSchemaName] = useState('');
  const [newFieldName, setNewFieldName] = useState('');
  const [newFieldType, setNewFieldType] = useState('number');
  const [newFieldLabel, setNewFieldLabel] = useState('');
  const [fields, setFields] = useState<{ name: string; label: string; type: string }[]>([
    { name: 'cable_meters', label: 'Длина кабеля (м)', type: 'number' },
    { name: 'photo_evidence', label: 'Фото трассы в лотке', type: 'image_webp' }
  ]);

  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    fetch('/api/v1/schemas')
      .then(r => r.json())
      .then(d => { if (d.success) setSchemas(d.data); });

    fetch('/api/v1/schemas/work-types')
      .then(r => r.json())
      .then(d => { if (d.success) setWorkTypes(d.data); });

    fetch('/api/v1/schemas/materials')
      .then(r => r.json())
      .then(d => { if (d.success) setMaterials(d.data); });
  }, []);

  const addField = () => {
    if (!newFieldName || !newFieldLabel) return;
    setFields([...fields, { name: newFieldName, label: newFieldLabel, type: newFieldType }]);
    setNewFieldName('');
    setNewFieldLabel('');
  };

  const handleSaveSchema = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newSchemaName) return;

    setSaving(true);
    try {
      const res = await fetch('/api/v1/schemas', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newSchemaName,
          category: 'daily_work',
          fieldsConfig: fields
        })
      });

      const data = await res.json();
      if (data.success) {
        setSuccess(true);
        setNewSchemaName('');
        setTimeout(() => setSuccess(false), 4000);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      {/* Portal Inner Subtabs */}
      <div className="flex bg-white p-1.5 rounded-2xl border border-slate-200/90 max-w-xl mx-auto shadow-sm gap-1.5">
        <button
          type="button"
          onClick={() => setActiveAdminSubTab('chessboard')}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl text-xs sm:text-sm font-semibold transition active:scale-95 ${
            activeAdminSubTab === 'chessboard'
              ? 'bg-purple-600 text-white shadow-md shadow-purple-600/20 font-extrabold'
              : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
          }`}
        >
          <Grid className="w-4 h-4" />
          <span>1. 2D-Шахматка стояков</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveAdminSubTab('settings')}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl text-xs sm:text-sm font-semibold transition active:scale-95 ${
            activeAdminSubTab === 'settings'
              ? 'bg-blue-600 text-white shadow-md shadow-blue-600/20 font-extrabold'
              : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
          }`}
        >
          <Settings className="w-4 h-4" />
          <span>2. No-Code Конструктор & Настройки</span>
        </button>
      </div>

      {activeAdminSubTab === 'chessboard' ? (
        <ChessboardModule />
      ) : (
        <div className="space-y-6">
          {/* Header */}
          <div className="bg-white p-4 rounded-2xl border border-slate-200/90 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] flex items-center justify-between">
            <div>
              <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <span className="p-2 bg-blue-50 text-blue-700 border border-blue-200 rounded-xl">
                  <Settings className="w-5 h-5" />
                </span>
                <span>Панель Управления: No-Code Конструктор & Справочники</span>
              </h2>
              <p className="text-xs text-slate-500 mt-1">
                Настройка динамических форм рапортов без хардкода, ведомости работ и спецификации материалов
              </p>
            </div>
          </div>

          {/* No-Code Schema Builder */}
          <div className="bg-white border border-slate-200/90 p-5 rounded-2xl space-y-4 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)]">
            <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
              <FileCode className="w-4 h-4 text-amber-600" />
              <span>Конструктор динамических шаблонов рапортов</span>
            </h3>

            {success && (
              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-800 text-xs font-semibold flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                <span>Новый динамический шаблон успешно сохранен в базе данных!</span>
              </div>
            )}

            <form onSubmit={handleSaveSchema} className="space-y-4 text-xs">
              <div>
                <label className="text-slate-600 font-semibold block mb-1">Наименование шаблона</label>
                <input
                  type="text"
                  placeholder="Например: Рапорт пусконаладочных работ (ПНР) и замеров"
                  value={newSchemaName}
                  onChange={(e) => setNewSchemaName(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl p-2.5 text-slate-900 font-bold focus:outline-none focus:border-amber-500"
                />
              </div>

              <div className="bg-slate-50/80 p-4 rounded-xl border border-slate-200 space-y-3">
                <span className="text-xs font-bold text-slate-700 block">Поля динамической формы:</span>

                <div className="space-y-2">
                  {fields.map((f, i) => (
                    <div key={i} className="flex items-center justify-between bg-white p-2.5 rounded-xl border border-slate-200 text-slate-800 shadow-sm">
                      <span><strong>{f.label}</strong> ({f.name})</span>
                      <span className="bg-amber-50 text-amber-800 border border-amber-200 px-2 py-0.5 rounded text-[10px] font-mono font-bold">{f.type}</span>
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-2 border-t border-slate-200">
                  <input
                    type="text"
                    placeholder="ID поля (напр. test_voltage)"
                    value={newFieldName}
                    onChange={(e) => setNewFieldName(e.target.value)}
                    className="bg-white border border-slate-200 rounded-xl p-2 text-slate-800"
                  />
                  <input
                    type="text"
                    placeholder="Подпись (напр. Напряжение мегомметра)"
                    value={newFieldLabel}
                    onChange={(e) => setNewFieldLabel(e.target.value)}
                    className="bg-white border border-slate-200 rounded-xl p-2 text-slate-800"
                  />
                  <div className="flex gap-2">
                    <select
                      value={newFieldType}
                      onChange={(e) => setNewFieldType(e.target.value)}
                      className="flex-1 bg-white border border-slate-200 rounded-xl p-2 text-slate-800 font-medium"
                    >
                      <option value="number">Числовое поле</option>
                      <option value="text">Текстовое поле</option>
                      <option value="image_webp">Фотоотчет (Canvas WebP)</option>
                      <option value="checkbox">Чекбокс (Да/Нет)</option>
                    </select>
                    <button
                      type="button"
                      onClick={addField}
                      className="px-3 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold rounded-xl transition shadow-sm"
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={saving || !newSchemaName}
                className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl shadow-md transition text-xs disabled:opacity-50 active:scale-95"
              >
                {saving ? 'Сохранение...' : 'Зарегистрировать шаблон рапорта'}
              </button>
            </form>
          </div>

          {/* Catalogs Overview */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Work Types */}
            <div className="bg-white border border-slate-200/90 p-4 rounded-2xl space-y-3 shadow-sm">
              <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center gap-1.5">
                <Layers className="w-4 h-4 text-blue-600" />
                <span>Справочник работ ЭОМ ({workTypes.length})</span>
              </h3>
              <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                {workTypes.map(w => (
                  <div key={w.id} className="bg-slate-50 p-2.5 rounded-xl border border-slate-200 text-xs flex items-center justify-between">
                    <div>
                      <div className="font-bold text-slate-900">{w.name}</div>
                      <div className="text-[10px] text-slate-500 font-mono">{w.code} | {w.rdCode}</div>
                    </div>
                    <span className="bg-amber-50 text-amber-800 border border-amber-200 px-2 py-0.5 rounded text-[10px] font-bold">{w.unit}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Materials */}
            <div className="bg-white border border-slate-200/90 p-4 rounded-2xl space-y-3 shadow-sm">
              <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center gap-1.5">
                <Package className="w-4 h-4 text-emerald-600" />
                <span>Справочник кабельной продукции & щитов ({materials.length})</span>
              </h3>
              <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                {materials.map(m => (
                  <div key={m.id} className="bg-slate-50 p-2.5 rounded-xl border border-slate-200 text-xs flex items-center justify-between">
                    <div>
                      <div className="font-bold text-slate-900">{m.name}</div>
                      <div className="text-[10px] text-slate-500">{m.manufacturer} • {m.specification}</div>
                    </div>
                    <span className="bg-emerald-50 text-emerald-800 border border-emerald-200 px-2 py-0.5 rounded text-[10px] font-bold">{m.unit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminModule;
